import Vue from "vue";
import Router from "vue-router";
import VueGtag from "vue-gtag";

import Onboard from "@/views/Onboard.vue";
import Plans from "@/views/Plans.vue";
import PlansHome from "@/components/plans/PlansHome.vue";
import ArchivedPlansHome from "@/components/plans/ArchivedPlansHome.vue";
import Plan from "@/views/Plan.vue";
import PlanHome from "@/components/plan/PlanHome.vue";
import PlanHistory from "@/components/plan/PlanHistory.vue";
import PlanEdit from "@/components/plan/PlanEdit.vue";
import ArchivedPlanHome from "@/components/plan/ArchivedPlanHome.vue";
import Group from "@/views/Group.vue";
import GroupHome from "@/components/group/GroupHome.vue";
import GroupHistory from "@/components/group/GroupHistory.vue";
import GroupEdit from "@/components/group/GroupEdit.vue";
import ArchivedGroupHome from "@/components/group/ArchivedGroupHome.vue";
import Project from "@/views/Project.vue";
import ProjectHome from "@/components/project/ProjectHome.vue";
import ProjectHistory from "@/components/project/ProjectHistory.vue";
import ProjectEdit from "@/components/project/ProjectEdit.vue";
import Analysis from "@/views/Analysis.vue";
import Profile from "@/views/Profile.vue";
import Notification from "@/views/Notification.vue";
import Invite from "@/components/onboard/Invite.vue";
import LoginForm from "@/components/onboard/LoginForm.vue";
import SignUpForm from "@/components/onboard/SignUpForm.vue";
import ProfileCreateForm from "@/components/onboard/ProfileCreateForm.vue";
import PassReset from "@/components/onboard/PassReset.vue";
import PassForgot from "@/components/onboard/PassForgot.vue";
import VerifyMessage from "@/components/onboard/VerifyMessage.vue";
import Maintenance from "@/components/onboard/Maintenance.vue";

// import Purchase from '@/views/Purchase.vue'

import { userService } from "@/services/user.service";
import { groupService } from "@/services/group.service";
import { planService } from "@/services/plan.service";
import Admin from "./views/Admin";
import { AdminRouter } from "./admin-router";
import Manual from "./views/Manual.vue";
import Error from "./views/Error.vue";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      name: "onboard",
      component: Onboard,
      children: [
        {
          path: "/:languageCode(en_US|ja_JP|zh_CN)*/login",
          name: "login",
          component: LoginForm,
        },
        {
          path: "/signup",
          name: "signup",
          component: SignUpForm,
        },
        {
          path: "/invite",
          name: "invite",
          component: Invite,
          beforeEnter: (to, from, next) => {
            if (to.query.type && to.query.token) {
              // Toss the token into session until we see the user.
              localStorage.setItem(to.query.type + "Invite", to.query.token);
            } else {
              console.error("No invitation detected");
              return next("/login");
            }
            return next();
          },
        },
        {
          path: "/verify",
          name: "verify",
          component: VerifyMessage,
        },
        {
          path: "/recovery",
          name: "recovery",
          component: PassForgot,
        },
        {
          path: "/reset",
          name: "reset",
          component: PassReset,
        },
        {
          path: "/profile-create",
          name: "profileCreate",
          component: ProfileCreateForm,
        },
        {
          path: "/maintenance",
          name: "maintenance",
          component: Maintenance,
        },
      ],
    },
    {
      path: "/logout",
      name: "logout",
    },
    {
      path: "/plans",
      component: Plans,
      children: [
        {
          path: "",
          name: "plans",
          component: PlansHome,
        },
        {
          path: "archive",
          component: ArchivedPlansHome,
        },
      ],
    },
    {
      path: "/plan/:planId",
      component: Plan,
      children: [
        {
          path: "",
          name: "plan",
          component: PlanHome,
        },
        {
          path: "history",
          component: PlanHistory,
        },
        {
          path: "edit",
          component: PlanEdit,
        },
        {
          path: "archive",
          component: ArchivedPlanHome,
        },
      ],
    },
    {
      path: "/group/:groupId",
      component: Group,
      children: [
        {
          path: "",
          name: "group",
          component: GroupHome,
        },
        {
          path: "history",
          component: GroupHistory,
        },
        {
          path: "edit",
          component: GroupEdit,
        },
        {
          path: "archive",
          component: ArchivedGroupHome,
        },
      ],
    },
    {
      path: "/project/:projectId",
      component: Project,
      children: [
        {
          path: "",
          name: "project",
          component: ProjectHome,
        },
        {
          path: "history",
          component: ProjectHistory,
        },
        {
          path: "edit",
          component: ProjectEdit,
        },
      ],
    },
    {
      path: "/project/:projectId/analysis/:moduleId",
      component: Analysis,
    },
    {
      path: "/profile",
      name: "profile",
      component: Profile,
    },
    {
      path: "/notification",
      name: "notification",
      component: Notification,
    },
    // Disabling but not removing, as they may want it back.
    /*{
      path: '/purchase',
      name: 'purchase',
      component: Purchase
    },*/
    {
      path: "/admin",
      name: "admin",
      component: Admin,
      children: AdminRouter.routes,
    },
    {
      path: "/manual",
      name: "manual",
      component: Manual,
    },
    {
      path: "/error",
      name: "error",
      component: Error,
    },
    {
      path: "*",
      redirect: "/",
    },
  ],
});

Vue.use(
  VueGtag,
  {
    config: { id: "GTM-TJKSVZM" },
  },
  router,
);

router.beforeEach((to, from, next) => {
  const lang = ["en_US", "ja_JP", "zh_CN"];
  const userPages = ["/login", "/invite", "/verify", "/recovery", "/reset"];
  let toPath = to.path;
  lang.forEach(function (el) {
    toPath = toPath.replace("/" + el, "");
  });
  const authReq = !userPages.includes(toPath);
  const groupInvite = localStorage.getItem("groupInvite");
  const planInvite = localStorage.getItem("planInvite");
  const loggedIn = localStorage.getItem("user");
  const me = JSON.parse(loggedIn);

  // Under maintenance
  if (to.path === "/maintenance") {
    return next();
  }

  if (to.path === "/logout") {
    localStorage.removeItem("user");
    return next("/login");
  }

  // Keep checking in with the api looking for timeouts.
  if (authReq) {
    userService.current().catch((err) => {
      console.error(err);
      localStorage.removeItem("user");
      localStorage.setItem("deauth", true);
      return next("/login");
    });
  }

  // Group invite handling if user is logged in.
  if (groupInvite && loggedIn && me.userInfo.countryOrRegion) {
    groupService
      .acceptInvitation(groupInvite)
      .then((invite) => {
        return next("/group/" + invite.id);
      })
      .catch((err) => {
        console.error("Invitation Acceptance Error: ", err);
      })
      .finally(() => {
        localStorage.removeItem("groupInvite");
      });
  }

  // Plan invite handling if user is logged in.
  if (planInvite && loggedIn && me.userInfo.countryOrRegion) {
    planService
      .acceptInvitation(planInvite)
      .then((invite) => {
        return next("/plan/" + invite.id);
      })
      .catch((err) => {
        console.error("Invitation Acceptance Error: ", err);
      })
      .finally(() => {
        localStorage.removeItem("planInvite");
      });
  }

  // Trying to hit a restricted page, prompt for Auth.
  if (authReq && !loggedIn) {
    return next("/login?dest=" + to.path);
  }

  // Trying to sign up without invite.
  if (!groupInvite && !planInvite && to.path === "/signup") {
    return next("/login");
  }

  // Trying to hit an onboarding page when logged in.
  if ((!authReq || to.path === "/") && loggedIn) {
    if (to.query.dest) {
      return next(to.query.dest);
    } else if (to.path === "/invite") {
      return next();
    }
    return next("/plans");
  }

  if (to.path.indexOf("/admin") !== -1 && me.userInfo.userType === 0) {
    return next("/plans");
  }

  // No profile? Better make one
  if (
    to.path !== "/profile-create" &&
    loggedIn &&
    !me.userInfo.countryOrRegion
  ) {
    return next("/profile-create");
  } else if (to.path === "/profile-create" && me.userInfo.countryOrRegion) {
    if (to.query.dest) {
      return next(to.query.dest);
    }
    return next("/plans");
  }

  // If token sent to /verify then make the verification call.
  if (to.path == "/verify" && !loggedIn && to.query.token) {
    userService.verify(to.query.token).then((user) => {
      if (user.token) {
        // success, login and redirect
        localStorage.setItem("user", JSON.stringify(user));
        return next("/plans");
      }
    });
  }

  if (loggedIn && me) {
    const existingHs = document.getElementById("hs-script-loader");
    if (!existingHs) {
      const hsScript = document.createElement("script");
      hsScript.id = "hs-script-loader";
      hsScript.type = "text/javascript";
      hsScript.async = true;
      hsScript.defer = true;
      hsScript.src = "//js.hs-scripts.com/47322292.js";
      document.head.appendChild(hsScript);
    }
  } else {
    document.getElementById("hs-script-loader")?.remove();
  }

  return next();
});

export default router;
